import StepCard from '../../../../components/wizard/StepCard';
import { useTranslation } from 'react-i18next';
import { WizardData } from '../../../../types/wizard';

interface Step8AvoidExperiencesProps {
  data: WizardData;
  onChange: (field: keyof WizardData, value: any) => void;
  onAdvance: () => void;
  error?: string;
}

const avoidOptions = [
  {
    id: 'multidoes',
    label: 'Multidões e Lugares Lotados',
    icon: 'ri-group-line',
    description: 'Evitar atrações turísticas muito populares e horários de pico'
  },
  {
    id: 'voos_longos',
    label: 'Voos Muito Longos',
    icon: 'ri-flight-takeoff-line',
    description: 'Preferir destinos com voos diretos ou conexões curtas'
  },
  {
    id: 'atividades_fisicas',
    label: 'Atividades Físicas Intensas',
    icon: 'ri-run-line',
    description: 'Evitar trilhas longas, escaladas e esportes radicais'
  },
  {
    id: 'calor_extremo',
    label: 'Calor Extremo',
    icon: 'ri-sun-line',
    description: 'Evitar destinos com temperaturas muito altas'
  },
  {
    id: 'frio_extremo',
    label: 'Frio Extremo',
    icon: 'ri-snowy-line',
    description: 'Evitar destinos com temperaturas muito baixas'
  },
  {
    id: 'vida_noturna',
    label: 'Vida Noturna Agitada',
    icon: 'ri-goblet-line',
    description: 'Preferir destinos mais tranquilos e silenciosos à noite'
  },
  {
    id: 'comida_exotica',
    label: 'Comida Muito Exótica',
    icon: 'ri-restaurant-line',
    description: 'Preferir gastronomia mais familiar e menos experimental'
  },
  {
    id: 'areas_urbanas',
    label: 'Grandes Centros Urbanos',
    icon: 'ri-building-4-line',
    description: 'Evitar cidades muito grandes e agitadas'
  },
  {
    id: 'isolamento',
    label: 'Lugares Muito Isolados',
    icon: 'ri-map-pin-line',
    description: 'Evitar destinos remotos com pouca infraestrutura'
  },
  {
    id: 'barulho',
    label: 'Ambientes Barulhentos',
    icon: 'ri-volume-up-line',
    description: 'Preferir lugares tranquilos e silenciosos'
  },
  {
    id: 'transporte_publico',
    label: 'Transporte Público Complexo',
    icon: 'ri-bus-line',
    description: 'Preferir destinos com transporte privado ou mais simples'
  },
  {
    id: 'idioma_dificil',
    label: 'Barreiras de Idioma',
    icon: 'ri-translate-2',
    description: 'Preferir destinos onde inglês ou português são falados'
  }
];

export default function Step8AvoidExperiences({ data, onChange, onAdvance, error }: Step8AvoidExperiencesProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage?.startsWith('en');
  const avoidExperiences = data.avoidExperiences || [];
  const localizedAvoidOptions = avoidOptions.map((option) => ({
    ...option,
    label:
      isEnglish
        ? ({
            multidoes: 'Crowds & Packed Places',
            voos_longos: 'Very Long Flights',
            atividades_fisicas: 'Intense Physical Activities',
            calor_extremo: 'Extreme Heat',
            frio_extremo: 'Extreme Cold',
            vida_noturna: 'Heavy Nightlife',
            comida_exotica: 'Very Exotic Food',
            areas_urbanas: 'Large Urban Centers',
            isolamento: 'Very Isolated Places',
            barulho: 'Noisy Environments',
            transporte_publico: 'Complex Public Transport',
            idioma_dificil: 'Language Barriers',
          } as Record<string, string>)[option.id]
        : option.label,
    description:
      isEnglish
        ? ({
            multidoes: 'Avoid very popular attractions and peak-hour crowds',
            voos_longos: 'Prefer destinations with direct flights or shorter connections',
            atividades_fisicas: 'Avoid long hikes, climbing and intense sports',
            calor_extremo: 'Avoid destinations with very high temperatures',
            frio_extremo: 'Avoid destinations with very low temperatures',
            vida_noturna: 'Prefer quieter evenings and calmer nights',
            comida_exotica: 'Prefer more familiar and less experimental food',
            areas_urbanas: 'Avoid very large and hectic cities',
            isolamento: 'Avoid remote destinations with limited infrastructure',
            barulho: 'Prefer calm and quieter places',
            transporte_publico: 'Prefer simpler or private transport logistics',
            idioma_dificil: 'Prefer places where English or Portuguese are spoken',
          } as Record<string, string>)[option.id]
        : option.description,
  }));

  const handleToggle = (optionId: string) => {
    const isSelected = avoidExperiences.includes(optionId);
    const updated = isSelected
      ? avoidExperiences.filter(id => id !== optionId)
      : [...avoidExperiences, optionId];
    onChange('avoidExperiences', updated);
    onChange('avoidanceStatus', updated.length > 0 ? 'provided' : 'not_answered');
  };

  const handleClear = () => {
    onChange('avoidExperiences', []);
    onChange('avoidanceStatus', 'not_answered');
  };

  return (
    <StepCard
      title={isEnglish ? 'Experiences to Avoid' : 'Experiências a Evitar'}
      description={isEnglish
        ? 'Select situations or experiences you would rather avoid during your trip. This step is optional.'
        : 'Selecione situações ou experiências que você prefere evitar durante sua viagem. Esta etapa é opcional.'}
    >
      <div className="space-y-6">
        {/* Counter */}
        <div className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-lg">
          <span className="text-sm text-white/90">{isEnglish ? 'Selected restrictions' : 'Restrições selecionadas'}</span>
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold text-white/90">
              {avoidExperiences.length}
            </span>
            {avoidExperiences.length > 0 && (
              <button
                type="button"
                onClick={handleClear}
                className="text-xs text-white/90 hover:text-white transition-colors whitespace-nowrap"
              >
                {isEnglish ? 'Clear' : 'Limpar'}
              </button>
            )}
          </div>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {localizedAvoidOptions.map((option) => {
            const isSelected = avoidExperiences.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleToggle(option.id)}
                className={`
                  relative p-4 rounded-xl border-2 transition-all duration-200 text-left
                  ${isSelected
                    ? 'bg-red-500/10 border-red-500 shadow-lg shadow-red-500/20'
                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                  }
                `}
              >
                {/* Check Icon */}
                {isSelected && (
                  <div className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center bg-red-500 rounded-full">
                    <i className="ri-close-line text-sm text-white"></i>
                  </div>
                )}

                {/* Icon */}
                <div className={`
                  w-12 h-12 flex items-center justify-center rounded-lg mb-3
                  ${isSelected ? 'bg-red-500/20' : 'bg-white/5'}
                `}>
                  <i className={`${option.icon} text-2xl ${isSelected ? 'text-red-400' : 'text-white/90'}`}></i>
                </div>

                {/* Label */}
                <h3 className={`text-sm font-semibold mb-1 ${isSelected ? 'text-white' : 'text-white/90'}`}>
                  {option.label}
                </h3>

                {/* Description */}
                <p className="text-xs text-white/90 leading-relaxed">
                  {option.description}
                </p>
              </button>
            );
          })}
        </div>

        {/* Skip Option */}
        {avoidExperiences.length === 0 && (
          <div className="text-center py-6">
            <button
              type="button"
              onClick={onAdvance}
              className="text-sm text-[#FFD700] hover:text-[#FFD700]/80 transition-colors whitespace-nowrap"
            >
              {isEnglish ? 'I do not have specific restrictions →' : 'Não tenho restrições específicas →'}
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <i className="ri-error-warning-line text-red-400"></i>
            <span className="text-sm text-red-400">{error}</span>
          </div>
        )}

        {/* Info Box */}
        <div className="p-4 bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-lg">
          <div className="flex gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-[#FFD700]"></i>
            </div>
            <div className="text-xs text-white/90 leading-relaxed">
              <strong className="text-white/90">{isEnglish ? 'Tip:' : 'Dica:'}</strong>{' '}
              {isEnglish
                ? 'Sharing your restrictions helps us create a more comfortable itinerary aligned with your preferences, while avoiding unpleasant situations.'
                : 'Compartilhar suas restrições nos ajuda a criar um roteiro mais confortável e alinhado com suas preferências, evitando situações desagradáveis.'}
            </div>
          </div>
        </div>
      </div>
    </StepCard>
  );
}
